/**
 * useLocation Hook
 * Manages GPS tracking, location permissions, and distance calculations
 */

import { useState, useEffect, useCallback } from "react";
import {
  startGPSTracking,
  stopGPSTracking,
  checkLocationPermission,
  calcDistance,
  openInGoogleMaps,
} from "../utils/gpsUtils";
import { DEFAULT_COORDINATES } from "./constants";

export const useLocation = () => {
  // Current GPS position
  const [currentLatitude, setCurrentLatitude] = useState(0);
  const [currentLongitude, setCurrentLongitude] = useState(0);
  const [currentAccuracy, setCurrentAccuracy] = useState(0);
  
  // Selected dealer location — null means no dealer selected yet
  const [dealerLatitude, setDealerLatitude] = useState(null);
  const [dealerLongitude, setDealerLongitude] = useState(null);
  
  // Location error state
  const [locationError, setLocationError] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [watchId, setWatchId] = useState(null);

  /**
   * Initialize GPS tracking
   * Production-aligned: Only sets locationError on permission denied,
   * not on transient GPS errors (indoor signal loss, timeout, etc.)
   */
  useEffect(() => {
    let id = null;
    let permissionStatus = null;

    const handleSuccess = ({ latitude, longitude, accuracy: acc }) => {
      setCurrentLatitude(latitude);
      setCurrentLongitude(longitude);
      setCurrentAccuracy(acc);
      // Only clear error if permission is not denied
      if (permissionStatus && permissionStatus.state !== "denied") {
        setLocationError(false);
      }
    };

    const handleError = (error) => {
      // Only set error if permission is denied, not on transient errors
      if (permissionStatus && permissionStatus.state === "denied") {
        setLocationError(true);
      }
      // Otherwise, do not set locationError to true for transient errors
    };

    const startTracking = () => {
      if ("geolocation" in navigator) {
        id = startGPSTracking(handleSuccess, handleError);
        setWatchId(id);
      } else {
        setLocationError(true);
      }
    };

    // Use Permissions API to determine denied state, then start tracking
    if (navigator.permissions) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          permissionStatus = status;
          if (status.state === "denied") {
            setLocationError(true);
          } else {
            setLocationError(false);
            startTracking();
          }
          status.onchange = () => {
            if (status.state === "denied") {
              setLocationError(true);
              if (id) stopGPSTracking(id);
            } else {
              setLocationError(false);
              startTracking();
            }
          };
        })
        .catch(() => {
          // Permissions API not available or failed, just try geolocation
          startTracking();
        });
    } else {
      startTracking();
    }

    // Cleanup on unmount
    return () => {
      if (id) {
        stopGPSTracking(id);
      }
      if (permissionStatus && permissionStatus.onchange) {
        permissionStatus.onchange = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Check location permission
   */
  const checkPermission = useCallback(async () => {
    const hasPermission = await checkLocationPermission();
    setLocationError(!hasPermission);
  }, []);

  /**
   * Set dealer location
   */
  const setDealerLocation = useCallback((lat, lng) => {
    // Only update if coordinates are valid numbers
    if (lat != null && !isNaN(lat)) {
      setDealerLatitude(lat);
    }
    if (lng != null && !isNaN(lng)) {
      setDealerLongitude(lng);
    }
  }, []);

  /**
   * Calculate distance between current position and dealer
   */
  const distanceToDealer = calcDistance(
    currentLatitude,
    currentLongitude,
    dealerLatitude,
    dealerLongitude
  );

  /**
   * Open dealer location in Google Maps
   */
  const openDealerInMaps = useCallback(() => {
    openInGoogleMaps(dealerLatitude, dealerLongitude);
  }, [dealerLatitude, dealerLongitude]);

  /**
   * Open current location in Google Maps
   */
  const openCurrentLocationInMaps = useCallback(() => {
    openInGoogleMaps(currentLatitude, currentLongitude);
  }, [currentLatitude, currentLongitude]);

  return {
    // Current position
    currentLatitude,
    currentLongitude,
    currentAccuracy,
    
    // Dealer location — null when no dealer selected (map uses current location)
    dealerLatitude,
    dealerLongitude,
    setDealerLocation,
    
    // Distance  
    distanceToDealer,
    
    // Location error
    locationError,
    checkPermission,
    
    // Map functions
    openDealerInMaps,
    openCurrentLocationInMaps,
  };
};

export default useLocation;
